/**
 * Gesturely — marketing media registry.
 *
 * Every clip below is a web derivative of one of the eight untouched recordings
 * in `media-source/`. <MediaFrame> starts each muted clip when it enters the
 * viewport, loops it while visible, and pauses it offscreen.
 */

export type GestureCueKind = 'swipe' | 'tap' | 'double-tap' | 'firm-press' | 'shape'

export interface GestureCue {
  kind: GestureCueKind
  label: string
  fingers: 1 | 3 | 4 | 5
  direction?: 'left' | 'right' | 'up' | 'down'
  delaySeconds: number
  durationSeconds?: number
}

export interface MediaClip {
  /** Path under `public/`, e.g. '/media/product/window-snap.mp4'. */
  src: string | null
  /** Smaller derivative selected by the browser on narrow viewports. */
  mobileSrc: string | null
  /** Deliberate opening still used while the real recording loads. */
  poster: string | null
  /** Timed, lightweight trackpad instructions synchronized to the footage. */
  cues: GestureCue[]
}

export const MEDIA = {
  /**
   * Primary window story: Safari moves through left, right, corner, and
   * maximized placements before settling.
   * Source: media-source/NEW-snapping to different grids.mov, 0.50–9.30s.
   */
  windowSnap: {
    src: '/media/product/window-snap.mp4?v=gesture-pass-2',
    mobileSrc: '/media/product/window-snap-mobile.mp4?v=performance-pass-1',
    poster: '/media/product/window-snap-poster.jpg?v=gesture-pass-2',
    // This is a fast montage of several placements, including corners. A
    // direction-by-direction overlay would imply gestures the footage does not
    // verify, so the explanatory gesture copy remains in the band below it.
    cues: [],
  },

  /**
   * Live destination prompts appear before Safari lands in each placement.
   * Source: media-source/NEW-visual grid prompts.mov, 1.40–7.70s.
   */
  zonePreview: {
    src: '/media/product/zone-preview.mp4?v=gesture-pass-2',
    mobileSrc: '/media/product/zone-preview-mobile.mp4?v=performance-pass-1',
    poster: '/media/product/zone-preview-poster.jpg?v=gesture-pass-2',
    cues: [],
  },

  /**
   * The available half, corner, maximize, and minimize destinations are
   * previewed in sequence.
   * Source: media-source/NEW-showing all grids.mov, 1.40–8.20s.
   */
  windowGrids: {
    src: '/media/product/window-grids.mp4?v=gesture-pass-2',
    mobileSrc: '/media/product/window-grids-mobile.mp4?v=performance-pass-1',
    poster: '/media/product/window-grids-poster.jpg?v=gesture-pass-2',
    cues: [],
  },

  /**
   * Safari transitions from a normal window into native Full Screen, then the
   * same gesture returns it to windowed mode. The recording does not show a
   * finger-count overlay, so the site describes the visible action separately
   * from its verified factory-default mapping.
   * Source: media-source/triple tap for full screen.mov, 3.40–11.40s.
   */
  fullScreen: {
    src: '/media/product/full-screen.mp4?v=gesture-pass-2',
    mobileSrc: '/media/product/full-screen-mobile.mp4?v=performance-pass-1',
    poster: '/media/product/full-screen-poster.jpg?v=gesture-pass-2',
    cues: [
      { kind: 'double-tap', label: 'Three-finger double-tap', fingers: 3, delaySeconds: 0.15, durationSeconds: 1.8 },
      { kind: 'double-tap', label: 'Three-finger double-tap', fingers: 3, delaySeconds: 3.45, durationSeconds: 1.8 },
    ],
  },

  /**
   * A real S is recognized in Gesturely, followed by the assigned Spotify
   * launch. One clean cut removes the intervening settings/desktop idle time.
   * Source: media-source/Draw shapes.mov, 18.20–22.20s + 24.80–29.40s.
   */
  drawnShape: {
    src: '/media/product/drawn-shape.mp4?v=gesture-pass-2',
    mobileSrc: '/media/product/drawn-shape-mobile.mp4?v=performance-pass-1',
    poster: '/media/product/drawn-shape-poster.jpg?v=gesture-pass-2',
    cues: [
      { kind: 'shape', label: 'Draw an S', fingers: 1, delaySeconds: 0.2, durationSeconds: 2.1 },
      { kind: 'shape', label: 'Draw an S', fingers: 1, delaySeconds: 4.25, durationSeconds: 2.1 },
    ],
  },

  /**
   * The complete app-launch flow: open the custom-gesture builder, choose and
   * confirm a five-finger tap, write and resolve "open safari app", save the
   * mapping, then perform the gesture on the desktop and watch Safari open.
   * Source: media-source/apps and links.mov, 2.50–44.40s. Continuous inactive
   * moments are accelerated and the settings portion is reframed for legibility.
   */
  appLaunch: {
    src: '/media/product/app-launch.mp4?v=gesture-pass-3',
    mobileSrc: '/media/product/app-launch-mobile.mp4?v=gesture-pass-3',
    poster: '/media/product/app-launch-poster.jpg?v=gesture-pass-3',
    cues: [
      { kind: 'tap', label: 'Five-finger tap', fingers: 5, delaySeconds: 26.2, durationSeconds: 1.8 },
    ],
  },

  /**
   * The full custom-gesture flow: select Firm Press, calibrate it, write and
   * resolve "mute volume", confirm the mapping, then perform the gesture three
   * times as the real macOS volume HUD confirms each mute/unmute change.
   * Source: media-source/firm press.mov, continuous 5.50–34.00s.
   */
  firmPress: {
    src: '/media/product/firm-press.mp4?v=gesture-pass-2',
    mobileSrc: '/media/product/firm-press-mobile.mp4?v=performance-pass-1',
    poster: '/media/product/firm-press-poster.jpg?v=gesture-pass-2',
    cues: [
      { kind: 'firm-press', label: 'Firm press', fingers: 1, delaySeconds: 20.9, durationSeconds: 1.6 },
      { kind: 'firm-press', label: 'Firm press', fingers: 1, delaySeconds: 22.9, durationSeconds: 1.6 },
      { kind: 'firm-press', label: 'Firm press', fingers: 1, delaySeconds: 24.65, durationSeconds: 1.6 },
    ],
  },

  /**
   * One continuous tester session recognizes Left Half, Right Half, and
   * Maximize, leaving all three successful results readable at the end.
   * Source: media-source/Gesture tester.mov, 7.30–16.60s.
   */
  gestureTester: {
    src: '/media/product/gesture-tester.mp4?v=gesture-pass-2',
    mobileSrc: '/media/product/gesture-tester-mobile.mp4?v=performance-pass-1',
    poster: '/media/product/gesture-tester-poster.jpg?v=gesture-pass-2',
    cues: [],
  },
} satisfies Record<string, MediaClip>
